import { computed, ref, watch } from 'vue';
import { useInternational } from '@/common/locale';
import { TabPanel } from '@/common/utils';
import { NotificationType } from '@/alert/utils/notification.types';
import {
  NOTIFICATION_ICON_KEY,
  NOTIFICATION_STATUS_I18N_KEY,
  NOTIFICATION_STATUS_METHOD_BY_TYPE,
  NOTIFICATION_STATUS_WINDOW_I18N_KEY,
} from '@/alert/utils/notification.define';
import { utcZeroTimeToStandardTime } from '@/common/utils/commonUtils';
import { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { DEFAULT_TRANSPORT_SERVER_TYPE } from '@/alert/utils/define';
import { isNil } from 'lodash-es';

import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { NotificationStatusItem } from '@/api-client/alert/model';
import { useGetV6NotificationStatusByMethodTypeAndResultId } from '@/api-client/alert/queries/notification-history-controller/notification-history-controller';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';

export interface Props {
  resultId: string;
  availableStatus: NotificationType[];
  initialSelectedType: NotificationType;
  timePeriodInfo: TimePeriodInfo;
  isShow: boolean;
}

interface OnSiteMessageValue {
  title: string;
  info: string;
  content: string;
}

type InfoItem =
  | { label: string; field: string; value: string; renderType: 'row' | 'viewer' }
  | { label: string; field: string; value: OnSiteMessageValue; renderType: 'onSiteMessage' };

interface InfoType {
  title?: string;
  info: InfoItem[];
}

export interface Emit {
  (e: 'update:isShow', value: boolean): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const selectedTab = ref<string>('');
  const tabPanels = ref<TabPanel<string>[]>([]);

  const isShow = computed<boolean>({
    get: () => props.isShow,
    set: (value) => emit('update:isShow', value),
  });

  const validateJson = (text: string) => {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  const getJsonStrValue = (value?: string) => {
    if (isNil(value)) return '';

    try {
      const parsed = JSON.parse(value);
      // NOTE: 2번 stringify 되어 응답하는 경우(웹훅 > requestMessage) 발견하여 parse 하는 로직 추가
      if (typeof parsed === 'string') {
        JSON.parse(parsed);
        return parsed;
      }
    } catch (error) {
      console.log(
        '[notificationStatusWindow] API 응답 형태가 변경됐을 수 있습니다. requestMessage JSON 파싱 실패:',
        error,
      );
    }

    return value;
  };

  // On-Site row에서 제외할 필드 목록 — 메시지 카드 영역에서 처리
  const ON_SITE_SKIP_FIELDS = new Set([
    'title',
    'content',
    'level',
    'ruleName',
    'ruleType',
    'ruleId',
    'pushId',
    'count',
    'userId',
  ]);

  const parseOnSiteMessage = (raw: string): OnSiteMessageValue => {
    if (!raw) return { title: '', info: '', content: '' };
    const lines = raw.split('\n');
    const title = lines[0] ?? '';
    // 빈 줄을 기준으로 본문과 정보 구분
    // 포맷: 제목\n본문\n\n정보
    const emptyLineIndex = lines.indexOf('', 1);
    if (emptyLineIndex === -1) {
      return { title, info: '', content: lines.slice(1).join('\n') };
    }
    return {
      title,
      content: lines.slice(1, emptyLineIndex).join('\n'),
      info: lines.slice(emptyLineIndex + 1).join('\n'),
    };
  };

  const buildOnSiteMessageValue = (rawData: any): OnSiteMessageValue => {
    const raw = rawData.requestMessage ?? '';
    if (raw) return parseOnSiteMessage(raw);
    return {
      title: rawData.title ?? rawData.ruleName ?? '',
      info: '',
      content: rawData.content ?? '',
    };
  };

  const getInfoFromRawData = (rawData: any) => {
    // NOTE: method 별로 응답형식이 다 다르기 때문에 axios 함수 반환타입 사용불가
    // NOTE: 임시 any 처리

    const isOnSite = selectedTab.value === 'onSite';
    const statusInfo: InfoType = { info: [] };
    const keys = Object.keys(rawData);
    keys.forEach((key) => {
      if (key === 'transportServerType') {
        statusInfo.title = rawData[key];
        return;
      }
      if (key === 'response' || key === 'requestMessage') {
        return;
      }
      // On-Site 전용 필드는 아래 메시지 카드에서 처리
      if (isOnSite && ON_SITE_SKIP_FIELDS.has(key)) {
        return;
      }
      let labelKey = key;
      let label = t(NOTIFICATION_STATUS_WINDOW_I18N_KEY[labelKey] || '') ?? labelKey;
      let value = rawData[key];
      if (key === 'notifiedMethod') {
        labelKey = NOTIFICATION_STATUS_METHOD_BY_TYPE[selectedTab.value];
        label = `${t(NOTIFICATION_STATUS_WINDOW_I18N_KEY[labelKey] || '') ?? labelKey} (${
          value.length || 0
        })`;
      }
      if (key === 'status') {
        value = value ? 'Succeeded' : 'Failed';
      } else if (key === 'receivers') {
        value = value.map(({ userId, userName }) => `${userName} (${userId})`);
        label = `${label} (${value.length || 0})`;
      } else if (key === 'sendTime') {
        value = utcZeroTimeToStandardTime(value);
      }

      statusInfo.info.push({
        label,
        field: key,
        value,
        renderType: 'row',
      });
    });

    if (isOnSite) {
      statusInfo.info.push({
        label: '',
        field: 'onSiteMessage',
        value: buildOnSiteMessageValue(rawData),
        renderType: 'onSiteMessage',
      });
      return statusInfo;
    }

    statusInfo.info.push({
      label: t(NOTIFICATION_STATUS_WINDOW_I18N_KEY.request || ''),
      field: 'request',
      value: getJsonStrValue(rawData.requestMessage),
      renderType: 'viewer',
    });

    statusInfo.info.push({
      label: t(NOTIFICATION_STATUS_WINDOW_I18N_KEY.response || ''),
      field: 'response',
      value: rawData.response ?? '',
      renderType: 'viewer',
    });

    return statusInfo;
  };

  const filterSmsType = (item: NotificationStatusItem, currentTab: string) => {
    const shouldShowSmsType =
      currentTab === 'sms' && item.transportServerType !== DEFAULT_TRANSPORT_SERVER_TYPE.sms;

    if (shouldShowSmsType) {
      return item;
    }

    const { smsType: _, ...rest } = item;
    return rest;
  };

  // Query params
  const methodType = computed(() => selectedTab.value as NotificationType);
  const resultId = computed(() => props.resultId);
  const queryParams = computed(() => {
    const { fromTimeUtc: fromTime, toTimeUtc: toTime } = props.timePeriodInfo;
    return {
      fromTime,
      toTime,
    };
  });

  // Vue Query 훅
  const { data: queryData, isPending } = useGetV6NotificationStatusByMethodTypeAndResultId(
    methodType,
    resultId,
    queryParams,
    {
      request: {
        frameName: FRAME_NAMES.NOTIFICATION_HISTORY_VIEW.DETAIL,
      },
      query: {
        meta: {
          onError: (err: unknown) => {
            errorHandlerWithMessage({ error: err });
            isShow.value = false;
          },
        },
        enabled: computed(
          () => props.isShow && !!selectedTab.value && !!props.resultId && selectedTab.value !== '',
        ),
        select: (response) => {
          const data = response?.data?.data ?? [];
          const currentTab = selectedTab.value;
          return (data.map((item) => filterSmsType(item, currentTab)) ?? []).map(
            getInfoFromRawData,
          );
        },
      },
    },
  );

  // Computed value for notificationStatusInfo
  const notificationStatusInfo = computed<InfoType[]>(() => {
    return queryData.value ?? [];
  });

  watch(isShow, (show) => {
    if (!show) {
      selectedTab.value = ''; // tab 초기화
      return;
    }
    selectedTab.value = props.initialSelectedType;
    tabPanels.value = props.availableStatus.map((status) => ({
      text: t(NOTIFICATION_STATUS_I18N_KEY[status] ?? ''),
      value: status,
      iconClass: NOTIFICATION_ICON_KEY[status] ?? '',
    }));
  });

  return {
    t,
    validateJson,

    isShow,
    selectedTab,
    tabPanels,
    notificationStatusInfo,
    isLoading: isPending,
  };
};
